<script setup>
import { ref, onMounted } from "vue";
import { Modal } from "bootstrap";

defineProps({
  detalle: { type: Object, default: null },
});

const modalEl = ref(null);
let modalInstance = null;

onMounted(() => {
  modalInstance = new Modal(modalEl.value);
});

defineExpose({
  show: () => modalInstance?.show(),
});

function formatFecha(value) {
  if (!value) return "—";
  const [y, m, d] = value.split("-");
  return `${d}/${m}/${y}`;
}
</script>

<template>
  <div class="modal fade" tabindex="-1" aria-hidden="true" ref="modalEl">
    <div
      class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg modal-offset-top"
    >
      <div class="modal-content divisan-modal border-0">
        <template v-if="detalle">
          <!-- HEADER -->
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title fw-bold text-cafe">
              <i class="bi bi-exclamation-triangle-fill me-2 text-verde"></i>
              Scrap #{{ detalle.id }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>

          <div class="modal-body pt-2">
            <!-- BLOQUE 1: fecha + operador en chips -->
            <div class="det-fila-top mb-3">
              <div class="det-chip">
                <i class="bi bi-calendar3 text-verde me-1"></i>
                <span class="det-chip-label">{{ formatFecha(detalle.fecha) }}</span>
              </div>
              <div class="det-chip det-chip-wide">
                <i class="bi bi-person-fill text-verde me-1"></i>
                <span class="det-chip-label">{{ detalle.operador_nombre }}</span>
              </div>
            </div>

            <!-- BLOQUE 2: producto, color, aditivo -->
            <div class="det-grid-3 mb-3">
              <div class="det-item">
                <span class="det-label">Producto</span>
                <span class="det-valor">{{ detalle.producto_nombre }}</span>
              </div>
              <div class="det-item">
                <span class="det-label">Color</span>
                <span class="det-valor d-flex align-items-center gap-2">
                  <span
                    v-if="detalle.color_hex"
                    class="color-swatch"
                    :style="{ background: detalle.color_hex }"
                  ></span>
                  {{ detalle.color_nombre }}
                </span>
              </div>
              <div class="det-item">
                <span class="det-label">Aditivo</span>
                <span :class="detalle.con_aditivo ? 'badge-si' : 'badge-no'">
                  {{ detalle.con_aditivo ? "Sí" : "No" }}
                </span>
              </div>
            </div>

            <!-- BLOQUE 3: cantidad scrap destacada -->
            <div class="det-item-scrap mb-3">
              <span class="det-label">Cantidad scrap</span>
              <span class="det-valor-scrap">
                {{ detalle.cantidad_scrap }} <small>{{ detalle.unidad_scrap }}</small>
              </span>
            </div>

            <!-- BLOQUE 4: códigos de defecto -->
            <div class="det-seccion-titulo">Códigos de defecto</div>
            <div
              v-if="detalle.codigos_defecto_label && detalle.codigos_defecto_label.length"
              class="badge-defectos-wrap mb-3"
            >
              <span
                v-for="(cod, idx) in detalle.codigos_defecto_label"
                :key="idx"
                class="badge-defecto"
              >
                {{ cod }}
              </span>
            </div>
            <div v-else class="text-muted-cafe small mb-3">Sin códigos registrados.</div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}
.text-muted-cafe {
  color: var(--d-brown);
  opacity: 0.6;
  font-size: 0.9rem;
}

.modal-offset-top {
  margin-top: 70px;
}

.divisan-modal {
  background-color: var(--d-cream);
  border: 2px solid var(--d-brown);
  border-radius: 20px;
}

.det-fila-top {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.det-chip {
  display: inline-flex;
  align-items: center;
  background: rgba(96, 108, 56, 0.1);
  border: 1px solid rgba(96, 108, 56, 0.25);
  border-radius: 8px;
  padding: 5px 12px;
  font-size: 0.85rem;
  color: var(--d-brown);
  font-weight: 600;
}
.det-chip-wide {
  flex: 1;
  min-width: 160px;
}
.det-chip-label {
  white-space: nowrap;
}

.det-grid-3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.det-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.det-label {
  font-size: 0.72rem;
  font-weight: 700;
  color: rgba(64, 34, 8, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.det-valor {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--d-brown);
}

.det-item-scrap {
  background: rgba(64, 34, 8, 0.06);
  border-radius: 10px;
  padding: 10px 14px;
  border-left: 3px solid var(--d-brown);
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.det-valor-scrap {
  font-size: 1.1rem;
  font-weight: 800;
  color: var(--d-brown);
}
.det-valor-scrap small {
  font-size: 0.78rem;
  font-weight: 600;
  opacity: 0.7;
}

.det-seccion-titulo {
  font-size: 0.72rem;
  font-weight: 800;
  color: var(--d-green);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 8px;
  padding-bottom: 4px;
  border-bottom: 2px solid rgba(96, 108, 56, 0.2);
}

.badge-defectos-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.badge-defecto {
  display: inline-block;
  background: rgba(64, 34, 8, 0.08);
  color: var(--d-brown);
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 0.82rem;
  font-weight: 600;
}

.badge-si {
  display: inline-block;
  background: #d4edda;
  color: #155724;
  border-radius: 6px;
  padding: 2px 10px;
  font-size: 0.8rem;
  font-weight: 700;
}
.badge-no {
  display: inline-block;
  background: #f8d7da;
  color: #721c24;
  border-radius: 6px;
  padding: 2px 10px;
  font-size: 0.8rem;
  font-weight: 700;
}

.color-swatch {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .modal-offset-top {
    margin-top: 60px;
  }
  .det-grid-3 {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
